import {
  AvailableInstance,
  InsertMapOverridesBody,
  InsertMapOverridesResponse,
  InstanceMappingDB,
  IntegratedEventsDB,
  IntegrationActionDB,
  IntegrationsMappedDB,
  IntegrationsMappedOptions,
  ITraySolution,
  UpdateMapOverridesBody,
  UpdateMapOverridesResponse,
  EMappedIntegrationTypes,
  ITraySolutionInstance,
  ITrayUser,
  InsertIntegrationsMappedBody,
  InsertIntegrationsMappedResponse,
  UpdateIntegrationsMappedBody,
  UpdateIntegrationsMappedResponse,
  EMappedIntegrationTypeIndex,
  EListTypes,
} from '@/types/integrations.types';
import { Delete, Get, Post, Put } from '../helpers';
import { RSVPLinkSync } from '@/types/rsvp.types';

export const FetchSolutions = async (
  channel: number
): Promise<{ solutions: ITraySolution[]; mappedSolutions: Record<string, ITraySolution> }> => {
  return Get({
    path: `/v3/admin/channel/integrations/solutions/${channel}`,
  });
};

export const FetchAvailableInstances = async (
  channel: number,
  eventUuid: string
): Promise<{ all_instances: AvailableInstance[] }> => {
  return Get({
    path: `/v3/admin/channel/integrations/event-instances/${channel}/${eventUuid}`,
  });
};

export async function FetchRSVPEventLink(eventUuid: string): Promise<{ rsvpLink: RSVPLinkSync }> {
  return Get({
    path: `/v3/admin/rsvp_link_sync/${eventUuid}`,
  });
}

export async function ApproveRSVPEventLink(linkGroup: string, eventId: number): Promise<RSVPLinkSync> {
  return Put({
    path: `/v3/admin/rsvp_link_sync/approve`,
    body: { linkGroup, eventId },
  });
}

export async function GetEventOverrides(
  instance_uuid: string,
  entity_uuid: string,
  channel: number
): Promise<InstanceMappingDB> {
  return Get({
    path: `/v3/admin/channel/integrations/mappings/${instance_uuid}/${entity_uuid}/${channel}`,
  });
}

export function GetSolutionActions(solution_uuid: string): Promise<IntegrationActionDB[]> {
  return Get({
    path: `/v3/admin/channel/integrations/solution-actions/${solution_uuid}`,
  });
}

export function SendTestRecord(
  channel: number,
  instance_mapped: IntegrationsMappedDB,
  instance_overrides?: IntegrationsMappedOptions
): Promise<{ response: string }> {
  return Post({
    path: `/v3/admin/channel/integrations/test-record`,
    body: { instance_mapped, instance_overrides, channel },
  });
}

export function ToggleEventIntegration(
  instance_uuid: string,
  event_uuid: string,
  channel: number,
  enable: boolean
): Promise<{ added?: IntegratedEventsDB[]; removed?: IntegratedEventsDB[] }> {
  if (enable) {
    return Post({
      path: `/v3/admin/channel/integrations/integration-event`,
      body: { instance_uuid, event_uuid, channel },
    });
  } else {
    return Delete({
      path: `/v3/admin/channel/integrations/integration-event`,
      body: { instance_uuid, event_uuid, channel },
    });
  }
}

export function CreateSolutionInstanceOverride(body: InsertMapOverridesBody): Promise<InsertMapOverridesResponse> {
  return Post({
    path: `/v3/admin/channel/integrations/overrides`,
    body,
  });
}

export function UpdateSolutionInstanceOverrides(body: UpdateMapOverridesBody): Promise<UpdateMapOverridesResponse> {
  return Put({
    path: `/v3/admin/channel/integrations/overrides`,
    body,
  });
}

export function GetSolutionInstances(userId: string): Promise<ITraySolutionInstance[]> {
  return Get({ path: `/v3/admin/channel/integrations/solution-instances/${userId}` });
}

export function GetChannelInstances(channel: number): Promise<IntegrationsMappedDB[]> {
  return Get({ path: `/v3/admin/channel/integrations/instances/${channel}` });
}

export function CreateSolutionInstance(
  channel: number,
  solutionId: string,
  solutionName: string,
  type: EMappedIntegrationTypes,
  trayUser: ITrayUser
): Promise<{ instance: ITraySolutionInstance; user: ITrayUser }> {
  return Post({
    url: `/v3/admin/channel/integrations/create/solution-instance`,
    body: { channel, userId: trayUser.tray_id, solutionId, solutionName, type, trayUser },
  });
}

export function CreateSolutionInstanceMapped(
  body: InsertIntegrationsMappedBody
): Promise<InsertIntegrationsMappedResponse> {
  return Post({ path: `/v3/admin/channel/integrations/mappings`, body });
}

export function UpdateSolutionInstanceMapped(
  body: UpdateIntegrationsMappedBody
): Promise<UpdateIntegrationsMappedResponse> {
  return Put({ path: `/v3/admin/channel/integrations/mappings`, body });
}

export function UpdateSolutionInstance(
  userId: string,
  instanceId: string,
  configName: string,
  configValue: string,
  mapping?: Record<string, number>,
  listType?: EListTypes
): Promise<void> {
  return Post({
    path: `/v3/admin/channel/integrations/solution-instance/update`,
    body: { userId, instanceId, configName, configValue, mapping, listType },
  });
}

export function ToggleInstance(
  userId: string,
  channel: number,
  instanceId: string,
  enabled: boolean,
  type?: EMappedIntegrationTypes,
  customMapping?: boolean,
  eventOverride?: boolean,
  event_uuid?: string
): Promise<ITraySolutionInstance> {
  return Post({
    path: `/v3/admin/channel/integrations/toggle-instance`,
    body: {
      userId,
      channel,
      type: type ? EMappedIntegrationTypeIndex[type] : undefined,
      instanceId,
      enabled,
      customMapping,
      eventOverride,
      event_uuid,
    },
  });
}

export function GetTrayUser(channel: number, channelName: string, channelUuid: string): Promise<ITrayUser> {
  return Post({ path: `/v3/admin/channel/integrations/user`, body: { channel, name: channelName, uuid: channelUuid } });
}

export function GetEmbedUrl(userId: string, instanceId: string): Promise<{ url: string }> {
  return Get({ path: `/v3/admin/channel/integrations/auth-window/${userId}/${instanceId}` });
}
